import React from 'react';
import { AlertTriangle, CheckCircle2, Copy, XCircle } from 'lucide-react';
import type { CdmoImportRowState } from '../../lib/cdmoImport/validateRow';
import type { CdmoImportOutcome } from '../../lib/cdmoImport/importRows';
import { EVIDENCE_STATUS, type EvidenceStatus } from '../../constants/cdmoImport';
import { OUTCOME_LABELS, ROW_STATE_LABELS } from './helpers';

const ROW_STATE_STYLES: Record<CdmoImportRowState, { className: string; Icon: typeof CheckCircle2 }> = {
  ready: { className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', Icon: CheckCircle2 },
  warning: { className: 'bg-amber-50 text-amber-800 ring-amber-600/20', Icon: AlertTriangle },
  error: { className: 'bg-red-50 text-red-700 ring-red-600/20', Icon: XCircle },
  duplicate: { className: 'bg-slate-100 text-slate-700 ring-slate-500/20', Icon: Copy },
};

export const StateBadge: React.FC<{ state: CdmoImportRowState }> = ({ state }) => {
  const { className, Icon } = ROW_STATE_STYLES[state];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${className}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
      {ROW_STATE_LABELS[state]}
    </span>
  );
};

const OUTCOME_STYLES: Record<CdmoImportOutcome, string> = {
  created: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  skipped_duplicate: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  skipped_error: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  skipped_by_admin: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  failed: 'bg-red-50 text-red-700 ring-red-600/20',
};

export const OutcomeBadge: React.FC<{ outcome: CdmoImportOutcome }> = ({ outcome }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${OUTCOME_STYLES[outcome]}`}>
    {OUTCOME_LABELS[outcome]}
  </span>
);

const EVIDENCE_STATUS_STYLES: Record<EvidenceStatus, string> = {
  [EVIDENCE_STATUS.VERIFIED]: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  [EVIDENCE_STATUS.INFERRED]: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  [EVIDENCE_STATUS.MIXED]: 'bg-sky-50 text-sky-800 ring-sky-600/20',
  [EVIDENCE_STATUS.UNKNOWN]: 'bg-slate-100 text-slate-700 ring-slate-500/20',
};

export const EvidenceStatusBadge: React.FC<{ status: EvidenceStatus | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${
      status ? EVIDENCE_STATUS_STYLES[status] : 'bg-white text-slate-gray ring-slate-300'
    }`}
  >
    {status ?? 'No status'}
  </span>
);

const URL_PATTERN = /(https?:\/\/[^\s<>"'|]+)/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

/**
 * Research text with its web addresses turned into links. Everything is rendered as React text
 * nodes — the CSV is untrusted, so nothing in it is ever interpreted as HTML — and only http(s)
 * addresses become links, opened in a new tab without access to this page.
 */
export const LinkifiedText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const parts = text.split(URL_PATTERN);
  return (
    <p className={`whitespace-pre-wrap break-words ${className ?? ''}`}>
      {parts.map((part, index) => {
        if (index % 2 === 0) return <React.Fragment key={index}>{part}</React.Fragment>;
        const trailing = part.match(TRAILING_PUNCTUATION)?.[0] ?? '';
        const href = trailing ? part.slice(0, -trailing.length) : part;
        return (
          <React.Fragment key={index}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-selected-blue underline decoration-selected-blue/40 underline-offset-2 hover:decoration-selected-blue break-all"
            >
              {href}
            </a>
            {trailing}
          </React.Fragment>
        );
      })}
    </p>
  );
};
